import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Check, X } from "lucide-react";
import { money } from "../lib/api";

const REC = {
  FIGHT: ["Fight — strong case", "#CCFF00"],
  FIGHT_WITH_ADDITIONAL_EVIDENCE: ["Fight with more evidence", "#FFB800"],
  ACCEPT: ["Accept / refund", "#F87171"],
};

const Gauge = ({ value, label, color, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-baseline justify-between">
      <span className="label-xs">{label}</span>
      <span className="font-mono text-3xl font-extrabold tabular" style={{ color }}>{value}%</span>
    </div>
    <div className="h-1.5 bg-line mt-2 rounded-sm overflow-hidden">
      <div className="h-full transition-[width] duration-700" style={{ width: `${value}%`, background: color }} />
    </div>
  </div>
);

const tabCls = "font-mono text-[11px] uppercase tracking-wider data-[state=active]:bg-line data-[state=active]:text-white";

export const BankNegotiationPanel = ({ a, bank }) => {
  const [recLabel, recColor] = REC[a.recommendation];
  return (
    <Tabs defaultValue="approval" data-testid="bank-negotiation-panel">
      <TabsList className="bg-field border border-line rounded-sm w-full justify-start">
        <TabsTrigger value="approval" className={tabCls} data-testid="tab-approval">Approval</TabsTrigger>
        <TabsTrigger value="strategy" className={tabCls} data-testid="tab-strategy">Strategy</TabsTrigger>
        <TabsTrigger value="matrix" className={tabCls} data-testid="tab-matrix">Evidence matrix</TabsTrigger>
      </TabsList>
      <TabsContent value="approval" className="space-y-5 pt-4">
        <Gauge label="Issuer approval likelihood" value={a.approval_likelihood} color={recColor} testid="approval-likelihood" />
        <Gauge label="Dispute strength" value={a.strength_score} color="#37E5D7" testid="strength-score" />
        <div className="grid grid-cols-3 gap-3 font-mono text-xs">
          <div><p className="label-xs">Issuer</p><p className="mt-1">{bank?.name}</p></div>
          <div><p className="label-xs">Issuer win %</p><p className="mt-1">{a.issuer_win_rate}%</p></div>
          <div><p className="label-xs">Exp. recovery</p><p className="mt-1" data-testid="expected-recovery">{money(a.expected_recovery)}</p></div>
        </div>
      </TabsContent>
      <TabsContent value="strategy" className="pt-4 space-y-4">
        <div className="p-4 border rounded-sm" style={{ borderColor: `${recColor}55` }} data-testid="recommendation">
          <p className="font-mono font-bold uppercase tracking-wider text-sm" style={{ color: recColor }}>{recLabel}</p>
          <p className="text-sm text-[#AAA] mt-1">{a.rationale}</p>
        </div>
        <ul className="space-y-2" data-testid="tactics-list">
          {a.tactics.map((t, i) => (
            <li key={i} className="text-sm text-[#CCC] flex gap-2"><span className="text-brand font-mono">{String(i + 1).padStart(2, "0")}</span>{t}</li>
          ))}
        </ul>
        {a.signals.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {a.signals.map((s) => <span key={s} className="px-2 py-0.5 border border-brand/30 text-brand font-mono text-[10px] rounded-sm">{s}</span>)}
          </div>
        )}
      </TabsContent>
      <TabsContent value="matrix" className="pt-4">
        <p className="text-xs text-white/45 mb-3">Required for {a.reason_label} · code {a.reason_code}</p>
        <ul className="space-y-2" data-testid="evidence-matrix">
          {a.required_evidence.map((r) => {
            const ok = a.present_evidence.includes(r);
            return (
              <li key={r} className="flex items-center justify-between py-2 border-b border-line font-mono text-xs">
                <span>{r.replace("_", " ")}</span>
                {ok ? <Check className="w-4 h-4 text-brand" /> : <X className="w-4 h-4 text-[#F87171]" />}
              </li>
            );
          })}
        </ul>
      </TabsContent>
    </Tabs>
  );
};
